/* What a source said it is running, read for a screen.
 *
 * **Five readings, and two of them are absences that must not be confused.** `unread` is a source no
 * successful verification has looked at — every connection registered before versions were read, and
 * every one nobody has verified since. `unreported` is a source that was reached and reported
 * nothing. A screen that drew either as an unsupported version would be blaming the source for a
 * reading that never happened.
 *
 * The verdict itself is the server's — `support`, graded against the floors it owns — so this file
 * holds no version numbers of its own and cannot drift from what the API decides. */

import type { TFunction } from 'i18next';

import type { SourceVersion } from '@/shared/api/generated/catalog';

export type LimitedFeature = SourceVersion['limited'][number];

export type VersionReading =
  | { readonly kind: 'unread' }
  | { readonly kind: 'unreported' }
  | { readonly kind: 'supported'; readonly text: string }
  | {
      readonly kind: 'limited';
      readonly text: string;
      readonly fullFrom: string | null;
      readonly limited: readonly LimitedFeature[];
    }
  | { readonly kind: 'unknown'; readonly text: string };

export function versionReading(version: SourceVersion | null): VersionReading {
  if (version === null) return { kind: 'unread' };
  const text = version.reported;
  if (text === null) return { kind: 'unreported' };
  switch (version.support) {
    case 'supported':
      return { kind: 'supported', text };
    case 'limited':
      return {
        kind: 'limited',
        text,
        fullFrom: version.full_support_from,
        limited: version.limited,
      };
    case 'unknown':
      return { kind: 'unknown', text };
  }
}

/** The version text a reading carries, or `null` for the two absences. */
export function versionText(reading: VersionReading): string | null {
  return reading.kind === 'unread' || reading.kind === 'unreported' ? null : reading.text;
}

/** One sentence about a reading: what it means for this source, in the reader's words. `product` is
 * the source family's label, so a limit reads "needs PostgreSQL 11 or later" rather than "needs 11". */
export function versionSentence(
  t: TFunction,
  reading: VersionReading,
  product: string,
): string {
  switch (reading.kind) {
    case 'unread':
      return t('connections.version.unread');
    case 'unreported':
      return t('connections.version.unreported');
    case 'supported':
      return t('connections.version.supported');
    case 'unknown':
      return t('connections.version.unknown');
    case 'limited': {
      const features = reading.limited.map((feature) =>
        t(`connections.version.feature.${feature}`),
      );
      return reading.fullFrom === null
        ? t('connections.version.limitedNoFloor', {
            features: features.join(', '),
            count: features.length,
          })
        : t('connections.version.limited', {
            features: features.join(', '),
            count: features.length,
            product,
            version: reading.fullFrom,
          });
    }
  }
}
